import { getElementTypesById } from "core-integration/src/store/element-types/reducers";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { ElementPhoto } from "plos/src/components/ElementPhoto";
import ButtonLink from "plos/src/components/links/ButtonLink";
import { externalLinkIcon } from "plos/src/components/links/links.css";
import { StatsList } from "plos/src/components/StatsList";
import ExternalIcon from "plos/src/img/icons/external.svg?react";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { fetchPlayer, fetchTeam } from "plos/src/utils/pulse";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MatchSummary } from "../MatchSummary";
import PlayerNotesDropdown from "../PlayerNotesDropdown";
import { PriceChangeSummary } from "../PriceChangeSummary";
import { getRankTypeString, getStatItems, StatItemType } from "../utils";
import {
  buttonLinkWrapper,
  externalLinkText,
  firstNameText,
  nameBlock,
  playerImageStyles,
  playerInfoStyles,
  positionText,
  profileHeaderInner,
  profileHeaderOuter,
  profileSummary,
  secondNameText,
  teamText,
} from "./profileSummary.css";
import {
  CtaUrls,
  PlayerInfo,
  ProfileSummaryLinkProps,
  ProfileSummaryProps,
} from "./types";
import { getDisplayName } from "./utils";

export const PlayerInfoSection = ({ position, team, element }: PlayerInfo) => {
  const { t } = useTranslation();
  const displayName = getDisplayName(element);

  return (
    <div className={playerInfoStyles}>
      <div className={positionText}>{position}</div>
      <h1 id="sheet-title" className={nameBlock}>
        <span className={visuallyHidden}>
          {t("profileSummary.playerProfileDisplayName", "Player Profile:")}
        </span>
        {displayName.showKnownName ? (
          <div className={secondNameText}>{displayName.knownName}</div>
        ) : (
          <>
            <div className={firstNameText}>{displayName.firstName}</div>
            <div className={secondNameText}>{displayName.secondName}</div>
          </>
        )}
      </h1>
      <div className={teamText}>{team}</div>
    </div>
  );
};

const PREVIEW_STATS = ["points_per_game", "form", "selected_by_percent"];

const ProfileSummaryLink = ({ href, label }: ProfileSummaryLinkProps) => {
  const { t } = useTranslation("common");
  return (
    <ButtonLink
      appearance="tonal"
      href={href}
      fullWidth
      size="small"
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className={externalLinkText}>{label}</span>
      <ExternalIcon className={externalLinkIcon} aria-hidden />
      <span className={visuallyHidden}>
        {t(
          "profileSummaryLink.externalSite",
          "(external site, opens in a new tab)",
        )}
      </span>
    </ButtonLink>
  );
};

const ProfileSummary = ({
  element,
  variant = "full-profile",
}: ProfileSummaryProps) => {
  const { t } = useTranslation();

  const [ctaUrls, setCtaUrls] = useState<CtaUrls>({
    profileUrl: null,
    shirtUrl: null,
  });

  const elementTypesById = useSelector(getElementTypesById);
  const elementType = elementTypesById[element.element_type];
  const { singular_name: position } = elementType;

  const currentEvent = useSelector(getCurrentEvent);
  const nextEvent = useSelector(getNextEvent);

  const teamsById = useSelector(getTeamsById);
  const team = teamsById[element.team].name;

  useEffect(() => {
    let active = true;

    // Clear previous player's URLs so a stale CTA is never shown while refetching
    setCtaUrls({ profileUrl: null, shirtUrl: null });

    const fetchPlayerUrls = async () => {
      // Fetch independently so a fallback (team) failure can't suppress the preferred (player) URL
      const [teamResult, playerResult] = await Promise.allSettled([
        fetchTeam(element.team_code),
        fetchPlayer(element.code),
      ]);

      if (!active) return;

      if (teamResult.status === "rejected") {
        console.error("Fetch failed:", teamResult.reason);
      }
      if (playerResult.status === "rejected") {
        console.error("Fetch failed:", playerResult.reason);
      }

      const playerMetadata =
        playerResult.status === "fulfilled"
          ? playerResult.value?.metadata
          : undefined;
      const teamMetadata =
        teamResult.status === "fulfilled"
          ? teamResult.value?.metadata
          : undefined;

      setCtaUrls({
        profileUrl: playerMetadata?.fantasy_web_profile_url ?? null,
        shirtUrl:
          playerMetadata?.fantasy_buy_player_shirt_url ??
          teamMetadata?.fantasy_shirt_url ??
          null,
      });
    };

    fetchPlayerUrls();

    return () => {
      active = false;
    };
  }, [element.code, element.team_code]);

  const { profileUrl, shirtUrl } = ctaUrls;

  const getEventId = () => {
    if (currentEvent) {
      return currentEvent.id;
    }
    if (nextEvent) {
      return nextEvent.id;
    }
    return null;
  };

  const eventId = getEventId();
  const { element_count: elementCount } = elementType;

  const stats: StatItemType[] = getStatItems({
    element,
    eventId,
    t,
  });
  const formattedStats = stats
    .filter((s) =>
      variant === "preview" ? PREVIEW_STATS.includes(s.id) : true,
    )
    .map(({ rankType, ...rest }) => ({
      ...rest,
      subValue: getRankTypeString({ elementCount, rankType }),
    }));

  return (
    <div className={profileSummary}>
      <div className={profileHeaderOuter}>
        <div className={profileHeaderInner}>
          <div className={playerImageStyles}>
            <ElementPhoto alt="" elementId={element.id} />
          </div>
          <PlayerInfoSection
            position={position}
            team={team}
            element={element}
          />
        </div>
      </div>
      <div className={buttonLinkWrapper}>
        {profileUrl && (
          <ProfileSummaryLink
            href={profileUrl}
            label={t("profileSummary.playerProfile", "Player Profile")}
          />
        )}
        {shirtUrl && (
          <ProfileSummaryLink
            href={shirtUrl}
            label={t("profileSummary.buyShirt", "Buy Player Shirt")}
          />
        )}
      </div>
      <PlayerNotesDropdown element={element} />
      <PriceChangeSummary element={element} />
      <StatsList stats={formattedStats} />
      <MatchSummary element={element} />
    </div>
  );
};

export default ProfileSummary;
